import type { RunState } from '~/types/game'
import { apiFetch } from '~/utils/api'
import { puzzleScore, type DailyClaim, type GauntletClaim, type ProgressClaim, type PuzzleClaim } from '~~/shared/leaderboard'
import { PROGRESS_EPOCH } from '~~/shared/progressEpoch'

/**
 * 🏆 Leaderboard (v1.16; server-authoritative writes in v1.18 per
 * Javi's brother). Reads go straight to Firestore with the client SDK;
 * writes go through POST /api/leaderboard/submit, where the server
 * verifies the ID token, validates the claim, computes the score and
 * writes with the Admin SDK. Client writes are denied by the rules.
 */
export interface LeaderboardEntry {
  uid: string
  name: string
  photo: string | null
  /** 🏷️🖼️ v6.45: the achievement whose title is worn, and the creature whose still is the avatar. */
  title?: string | null
  avatar?: string | null
  ascension: number
  act: number
  nodesCleared: number
  victory: boolean
  playtimeMs: number
  score: number
}

function claimFrom(run: RunState): ProgressClaim {
  return {
    ascension: run.ascension ?? 0,
    act: run.act ?? 1,
    nodesCleared: run.completedNodeIds.length,
    victory: !!run.bossDefeated,
    playtimeMs: Math.round(run.playtimeMs ?? 0),
    // ⏱️ v5.23: lets the server clamp the time to the run's real span.
    ...(run.runId ? { runId: run.runId } : {}),
  }
}

/** ⏱️ v5.23: stamps a fresh run's birth with the SERVER clock so the
 * end-of-run submit can clamp claimed play-time to real wall-clock.
 * Fire-and-forget; a signed-out or offline start simply goes unstamped
 * (no clamp — the board stays fail-open like every claim on it). */
export async function registerRunStart(runId: string | undefined) {
  if (!import.meta.client || !runId) return
  const current = (await firebaseReady()).auth.currentUser
  // 🛡️ v6.42: anonymous sessions stamp too (the v6.39 refusal was about
  // board ROWS) - a run started signed-out and linked to Google later
  // keeps its uid, so the stamp is the one the board guard will look for.
  if (!current) return
  try {
    const token = await current.getIdToken()
    await apiFetch('/api/leaderboard/run-start', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
      body: { runId },
    })
  } catch (error) {
    console.warn('Run-start stamp failed.', error)
  }
}

/** Reports the run's progress to the server, which stores it only when
 * it beats the player's best. Safe to call often; no-ops signed out. */
export async function submitProgress(run: RunState | null | undefined) {
  if (!import.meta.client || !run) return
  const current = (await firebaseReady()).auth.currentUser
  // 🛡️ v6.39: boards are a signed-in feature - the server refuses the
  // lazy anonymous identity (403), so do not spend the round trip.
  if (!current || current.isAnonymous) return
  try {
    const token = await current.getIdToken()
    // 🎲 Daily runs (v5.54) report to the day's own board — unlimited
    // attempts, the row keeps the best (beatsStored server-side).
    if (run.mode === 'daily' && run.dailyDate) {
      const claim: DailyClaim = {
        day: run.dailyDate,
        act: run.act ?? 1,
        nodesCleared: run.completedNodeIds.length,
        victory: !!run.bossDefeated,
        playtimeMs: Math.round(run.playtimeMs ?? 0),
        ...(run.runId ? { runId: run.runId } : {}),
      }
      await apiFetch('/api/leaderboard/daily-submit', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { ...claim, epoch: PROGRESS_EPOCH },
      })
      return
    }
    // ⚔️ Gauntlet runs (v2.34) report to their own board — their act
    // range would fail the expedition claim's validation anyway.
    if (run.gauntlet) {
      const claim: GauntletClaim = {
        act: run.act ?? 1,
        nodesCleared: run.completedNodeIds.length,
        playtimeMs: Math.round(run.playtimeMs ?? 0),
        // 🛡️ v6.42: lets the server match the run to its start stamp.
        ...(run.runId ? { runId: run.runId } : {}),
      }
      await apiFetch('/api/leaderboard/gauntlet-submit', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        // 🧨 v3.96: claims carry the progress era; the server refuses
        // pre-wipe sessions (the door Javi's friend walked through).
        body: { ...claim, epoch: PROGRESS_EPOCH },
      })
      return
    }
    await apiFetch('/api/leaderboard/submit', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
      body: { ...claimFrom(run), epoch: PROGRESS_EPOCH },
    })
  } catch (error) {
    // Offline / endpoint unavailable — never let the game feel it.
    console.warn('Leaderboard submit failed.', error)
  }
}

/** ⚔️ Gauntlet board (v2.34): DEEPEST act wins; nodes cleared then
 * playtime break ties. */
export interface GauntletLeaderboardEntry {
  uid: string
  name: string
  photo: string | null
  /** 🏷️🖼️ v6.45: the achievement whose title is worn, and the creature whose still is the avatar. */
  title?: string | null
  avatar?: string | null
  act: number
  nodesCleared: number
  playtimeMs: number
  score: number
}


/** 🏆 v6.41 (audit P1-1): the boards come from ONE cached server read
 * (/api/leaderboard/top) instead of a 50-document Firestore query per
 * visitor. The route drops stale eras and shapes rows exactly as this
 * file used to; ordering and the derived puzzle score stay here. */
async function topRows<T>(board: string, week?: string): Promise<T[]> {
  const res = await apiFetch<{ rows: T[] }>('/api/leaderboard/top', { query: week ? { board, week } : { board } })
  return res.rows
}

export async function fetchGauntletLeaderboard(): Promise<GauntletLeaderboardEntry[]> {
  const rows = await topRows<GauntletLeaderboardEntry>('gauntlet')
  return rows.sort((a, b) => b.score - a.score || a.playtimeMs - b.playtimeMs)
}

export async function fetchLeaderboard(): Promise<LeaderboardEntry[]> {
  const rows = await topRows<LeaderboardEntry>('run')
  // Ties break toward the faster run.
  return rows.sort((a, b) => b.score - a.score || a.playtimeMs - b.playtimeMs)
}

/** 🗓️ Expedition board (v5.54 as daily; WEEKLY since v5.63): one board
 * per UTC week (doc id = the week's Monday), everyone on the same
 * seeded run. Victory beats depth; the stopwatch breaks ties. The
 * `weeklyLeaderboard` collection is NEW in v5.63 — Monday dates had
 * already been daily board ids, so reusing `dailyLeaderboard` would
 * have mixed two different worlds on one board. */
export interface DailyLeaderboardEntry {
  uid: string
  name: string
  photo: string | null
  /** 🏷️🖼️ v6.45: the achievement whose title is worn, and the creature whose still is the avatar. */
  title?: string | null
  avatar?: string | null
  act: number
  nodesCleared: number
  victory: boolean
  playtimeMs: number
  score: number
}

export async function fetchDailyLeaderboard(day: string): Promise<DailyLeaderboardEntry[]> {
  const rows = await topRows<DailyLeaderboardEntry>('weekly', day)
  return rows.sort((a, b) => b.score - a.score || a.playtimeMs - b.playtimeMs)
}

/** 🥇 Ask the server whether yesterday('s) board owes us a top-3 payout.
 * Fire-and-forget from the title screen; dedup'd server-side forever. */
export async function claimDailyTop3(day: string): Promise<{ rank: number | null; paid: number } | null> {
  if (!import.meta.client) return null
  const current = (await firebaseReady()).auth.currentUser
  if (!current) return null
  try {
    const token = await current.getIdToken()
    return await apiFetch<{ rank: number | null; paid: number }>('/api/daily/claim-top3', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
      body: { day },
    })
  } catch {
    return null
  }
}

/** 🧩 Puzzle Game board (v1.28): ranks ASCENDING — lowest score wins. */
export interface PuzzleLeaderboardEntry {
  uid: string
  name: string
  photo: string | null
  /** 🏷️🖼️ v6.45: the achievement whose title is worn, and the creature whose still is the avatar. */
  title?: string | null
  avatar?: string | null
  highestLevel: number
  damageTotal: number
  score: number
}

export async function submitPuzzleProgress(claim: PuzzleClaim) {
  if (!import.meta.client) return
  const current = (await firebaseReady()).auth.currentUser
  // 🛡️ v6.39: boards are a signed-in feature - the server refuses the
  // lazy anonymous identity (403), so do not spend the round trip.
  if (!current || current.isAnonymous) return
  try {
    const token = await current.getIdToken()
    await apiFetch('/api/leaderboard/puzzle-submit', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
      body: { ...claim, epoch: PROGRESS_EPOCH },
    })
  } catch (error) {
    console.warn('Puzzle leaderboard submit failed.', error)
  }
}

export async function fetchPuzzleLeaderboard(): Promise<PuzzleLeaderboardEntry[]> {
  const rows = await topRows<PuzzleLeaderboardEntry>('puzzle')
  // 🧮 v4.02: scores are DERIVED under the current ladder, not read —
  // rows written while the ladder was 25 levels carry 25-based numbers.
  return rows
    .map((row) => {
      row.score = puzzleScore({ highestLevel: row.highestLevel ?? 0, damageTotal: row.damageTotal ?? 0 })
      return row
    })
    .sort((a, b) => a.score - b.score)
}

/** 🏆 Achievements board (v7.99, per Javi): the count of achievements earned
 * ranks, the earlier arrival at a count wins ties (the server folds the moment it
 * first saw the count into the score). */
export interface AchievementsLeaderboardEntry {
  uid: string
  name: string
  photo: string | null
  title?: string | null
  avatar?: string | null
  count: number
  reachedAt: number
  score: number
}

// One claim per distinct ledger per session: the title re-evaluates on every visit.
let lastAchievementsClaim = ''

/** Reports the ledger's id list; the server stores it only when the count grew.
 * Safe to call often; no-ops signed out or unchanged. */
export async function submitAchievements(ids: string[]) {
  if (!import.meta.client || ids.length === 0) return
  const claim = [...new Set(ids)].sort()
  const key = claim.join(',')
  if (key === lastAchievementsClaim) return
  const current = (await firebaseReady()).auth.currentUser
  // 🛡️ v6.39: boards are a signed-in feature - the server refuses the
  // lazy anonymous identity (403), so do not spend the round trip.
  if (!current || current.isAnonymous) return
  lastAchievementsClaim = key
  try {
    const token = await current.getIdToken()
    await apiFetch('/api/leaderboard/achievements-submit', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
      body: { ids: claim, epoch: PROGRESS_EPOCH },
    })
  } catch (error) {
    lastAchievementsClaim = ''
    console.warn('Achievements leaderboard submit failed.', error)
  }
}

export async function fetchAchievementsLeaderboard(): Promise<AchievementsLeaderboardEntry[]> {
  const rows = await topRows<AchievementsLeaderboardEntry>('achievements')
  return rows.sort((a, b) => b.score - a.score)
}
